"use client";

import type * as React from "react";
import { Tabs, TabsList, TabsTrigger } from "../../atoms/tabs";

export type McpSearchScope = "servers" | "tools";

export type McpSearchScopeTabsProps = {
  active: McpSearchScope;
  serverCount: number;
  toolCount: number;
  onChange?: (scope: McpSearchScope) => void;
} & Omit<
  React.ComponentProps<typeof Tabs>,
  "value" | "onValueChange" | "onChange"
>;

/**
 * MCP servers / Tools scope switcher shown above search results. Composes the
 * design-system `Tabs` primitive (line variant) instead of reimplementing
 * tablist semantics — so the underline-on-active look, keyboard nav, and
 * focus-ring all come from the same source as every other tab in the app.
 * It renders tabs and picks one-of-two, so it's named `…Tabs`, not `…Toggle`.
 */
export function McpSearchScopeTabs({
  active,
  serverCount,
  toolCount,
  onChange,
  ...rest
}: McpSearchScopeTabsProps) {
  return (
    <Tabs
      {...rest}
      aria-label="Search result scope"
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      onValueChange={(value) => onChange?.(value as McpSearchScope)}
      value={active}
    >
      <TabsList variant="line">
        <TabsTrigger value="servers">
          MCP servers
          <span className="text-muted-foreground tabular-nums">
            {serverCount}
          </span>
        </TabsTrigger>
        <TabsTrigger value="tools">
          Tools
          <span className="text-muted-foreground tabular-nums">
            {toolCount}
          </span>
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
